import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Breadcrumbs from '../components/Breadcrumbs';
import { FaSearch, FaPhone, FaEnvelope, FaUser } from 'react-icons/fa';
import api from '../services/api';
import CleroFoto from '../components/CleroFoto';
import { mergeCleroConAutoridades } from '../utils/cleroFotos';
import { directorioClero as directorioCleroEstatico } from '../data/directorioClero';
import './Clero.css';

const CLERO_ID_BASE = 100000;

// Imagen por defecto: silueta de persona (mientras se actualiza la foto)
const DEFAULT_AVATAR = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23999'%3E%3Cpath d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z'/%3E%3C/svg%3E";

const Clero = () => {
  const [busqueda, setBusqueda] = useState('');

  const {
    data: directorioApi,
    isLoading: cargandoDirectorio,
    isError: errorDirectorio
  } = useQuery({
    queryKey: ['directorio-clero-public'],
    queryFn: async () => {
      const res = await api.get('/directorio-clero');
      return Array.isArray(res.data) ? res.data : [];
    },
    staleTime: 60 * 1000,
    retry: 2,
    refetchOnMount: 'always'
  });

  const { data: autoridades = [] } = useQuery({
    queryKey: ['autoridades-fotos-clero'],
    queryFn: async () => {
      const res = await api.get('/autoridades', { params: { _: Date.now() } });
      return Array.isArray(res.data) ? res.data : [];
    },
    staleTime: 60 * 1000,
    retry: 2,
    refetchOnMount: 'always'
  });

  const directorioClero = useMemo(() => {
    const base =
      Array.isArray(directorioApi) && directorioApi.length > 0
        ? directorioApi
        : directorioCleroEstatico;
    return mergeCleroConAutoridades(base, autoridades);
  }, [directorioApi, autoridades]);

  const listaFiltrada = useMemo(() => {
    if (!busqueda.trim()) return directorioClero;
    const q = busqueda.toLowerCase().trim();
    return directorioClero.filter(
      p =>
        (p.nombreCompleto && p.nombreCompleto.toLowerCase().includes(q)) ||
        (p.cargo && p.cargo.toLowerCase().includes(q)) ||
        (p.celular && p.celular.replace(/\s/g, '').includes(q)) ||
        (p.email && p.email.toLowerCase().includes(q))
    );
  }, [busqueda]);

  const formatTel = (celular) => {
    if (!celular || !celular.trim()) return null;
    const digits = celular.replace(/\D/g, '');
    if (digits.length >= 10) return digits;
    return celular.trim();
  };

  return (
    <div className="clero-page">
      <Breadcrumbs />

      <section className="section clero-hero">
        <div className="container">
          <h1 className="page-title">Directorio Presbiteral</h1>
          <p className="section-intro">
            Diócesis de Ipiales. Busque por nombre, celular o correo electrónico.
          </p>
          <div className="clero-siid-callout">
            <p>
              Para consultar <strong>autoridades, misioneros, párrocos y voluntarios</strong> en formato de perfiles (administrable desde el admin), utilice el <strong>Sistema Integrado de Información Diocesana (SIID)</strong>:
            </p>
            <a href="/search-profile" target="_blank" rel="noopener noreferrer" className="btn clero-siid-btn">
              Abrir búsqueda de perfiles (SIID) →
            </a>
          </div>

          <div className="clero-buscador">
            <div className="search-box clero-search-box">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Buscar por nombre, celular o email..."
                value={busqueda}
                onChange={e => setBusqueda(e.target.value)}
                className="search-input"
                aria-label="Buscar en directorio presbiteral"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="section clero-listado">
        <div className="container">
          {cargandoDirectorio && directorioClero.length === 0 && (
            <p className="clero-loading" role="status">Cargando directorio…</p>
          )}
          {errorDirectorio && !cargandoDirectorio && (
            <p className="clero-api-aviso" role="alert">
              No se pudo conectar con el servidor. Se muestran datos guardados en el sitio; las fotos se toman de Autoridades cuando existan.
            </p>
          )}
          {!cargandoDirectorio && busqueda.trim() && listaFiltrada.length === 0 ? (
            <div className="clero-no-results">
              <p>No se encontraron registros con ese criterio.</p>
            </div>
          ) : listaFiltrada.length > 0 ? (
            <>
              <div className="clero-results-header">
                <h2>Resultados</h2>
                <p>{listaFiltrada.length} registro{listaFiltrada.length !== 1 ? 's' : ''} en el directorio</p>
              </div>
              <div className="clero-cards">
                {listaFiltrada.map((p, idx) => {
                  const tel = formatTel(p.celular);
                  const perfilQrId =
                    p.id != null && Number(p.id) > 0 && Number(p.id) < CLERO_ID_BASE
                      ? `parroco-${CLERO_ID_BASE + Number(p.id)}`
                      : null;
                  return (
                    <article key={p.id != null ? `clero-${p.id}` : p.numero != null ? p.numero : `obispo-${idx}`} className="clero-card">
                      <div className="clero-card-photo">
                        {perfilQrId ? (
                          <Link to={`/qr/${perfilQrId}`} className="clero-card-photo-link" title="Ver perfil SIID">
                            <CleroFoto
                              fotoUrl={p.foto_url}
                              alt={p.nombreCompleto || ''}
                              defaultAvatar={DEFAULT_AVATAR}
                            />
                          </Link>
                        ) : (
                          <CleroFoto
                            fotoUrl={p.foto_url}
                            alt={p.nombreCompleto || ''}
                            defaultAvatar={DEFAULT_AVATAR}
                          />
                        )}
                      </div>
                      <div className="clero-card-info">
                        {p.numero != null && <span className="clero-card-num">No. {p.numero}</span>}
                        <h3 className="clero-card-name">
                          {perfilQrId ? (
                            <Link to={`/qr/${perfilQrId}`} className="clero-card-profile-link">
                              {p.nombreCompleto}
                            </Link>
                          ) : (
                            p.nombreCompleto
                          )}
                        </h3>
                        {p.cargo && <p className="clero-card-role">{p.cargo}</p>}
                        {p.fechaNacimiento && <p className="clero-card-meta"><strong>Nac.:</strong> {p.fechaNacimiento}</p>}
                        {p.fechaOrdenacion && <p className="clero-card-meta"><strong>Ord.:</strong> {p.fechaOrdenacion}</p>}
                        {tel ? (
                          <p className="clero-card-contact">
                            <a href={`tel:${tel}`} className="clero-link"><FaPhone className="icon-phone-upright" /> {p.celular}</a>
                          </p>
                        ) : p.celular ? <p className="clero-card-contact">{p.celular}</p> : null}
                        {p.email && p.email.trim() ? (
                          <p className="clero-card-contact">
                            <a href={`mailto:${p.email.trim()}`} className="clero-link"><FaEnvelope /> {p.email.trim()}</a>
                          </p>
                        ) : null}
                      </div>
                    </article>
                  );
                })}
              </div>
              <p className="clero-editar-nota">
                <Link to="/contacto">Sugerir corrección o actualización</Link> del directorio.
              </p>
            </>
          ) : !cargandoDirectorio ? (
            <div className="clero-no-results">
              <p>No hay registros en el directorio en este momento.</p>
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );
};

export default Clero;
